import React, { useMemo, useState, createElement } from 'react';
import { ExternalLink, Lock, X, Check, Plus, ToggleRight, ToggleLeft, ChevronRight, ChevronUp, ChevronDown, Trash2, Globe, ShieldCheck, AlertCircle, Clock, CheckCircle, FileCheck, GraduationCap, FileText, Upload, Save, Lightbulb, AlertTriangle, Search, Camera, Eye, Monitor, Smartphone, Star, MessageSquare, Calendar, Video, Mic, BarChart3, Pin, EyeOff, ArrowRight, Sparkles, MapPin, ShoppingBag } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useAstrologerProfile } from '../../contexts/AstrologerProfileContext';
interface ProfileTabProps {
  showToast: (msg: string) => void;
  onNavigate: (page: string) => void;
  setActiveTab?: (tab: string) => void;
}
interface ProfileBlock {
  id: string;
  label: string;
  enabled: boolean;
  location: string;
}
const ALL_SPECIALIZATIONS = ['Западная астрология', 'Натальная карта', 'Прогностика', 'Синастрия', 'Ведическая астрология', 'Хорарная астрология', 'Элективная астрология', 'Кармическая астрология', 'Медицинская астрология', 'Финансовая астрология', 'Детская астрология', 'Астропсихология', 'Нумерология', 'Таро', 'Астрокартография', 'Лунная астрология', 'Бизнес-астрология'];
const RECOMMENDED_ORDER = ['about', 'services', 'reviews', 'products', 'education', 'faq'];
const MAX_ABOUT_LENGTH = 1000;
export function ProfileTab({
  showToast,
  onNavigate,
  setActiveTab
}: ProfileTabProps) {
  const {
    profile,
    updateProfile
  } = useAstrologerProfile();
  // Preview mode
  const [showPreview, setShowPreview] = useState(false);
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [highlightedBlock, setHighlightedBlock] = useState<string | null>(null);
  // Main info — initialized from context
  const [firstName, setFirstName] = useState(profile.firstName);
  const [lastName, setLastName] = useState(profile.lastName);
  const [experience, setExperience] = useState(profile.experience);
  const [languages, setLanguages] = useState(profile.languages);
  const [aboutText, setAboutText] = useState(profile.aboutText);
  const [showAboutHint, setShowAboutHint] = useState(false);
  // Specializations
  const [specializations, setSpecializations] = useState(profile.specializations);
  const [specSearch, setSpecSearch] = useState('');
  const [showSpecDropdown, setShowSpecDropdown] = useState(false);
  const [customSpecInput, setCustomSpecInput] = useState('');
  const [showCustomSpecInput, setShowCustomSpecInput] = useState(false);
  // Avatar
  const [avatarUrl, setAvatarUrl] = useState<string | null>(profile.avatarUrl);
  const handleAvatarUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = e => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        if (file.size > 5 * 1024 * 1024) {
          showToast('⚠️ Файл слишком большой (макс. 5 МБ)');
          return;
        }
        const url = URL.createObjectURL(file);
        setAvatarUrl(url);
        showToast('Фото профиля обновлено ✓');
      }
    };
    input.click();
  };
  // Contacts — initialized from context
  const [contacts, setContacts] = useState(profile.contacts);
  const [showChatButton, setShowChatButton] = useState(profile.showChatButton);
  const [showBookButton, setShowBookButton] = useState(profile.showBookButton);
  // Consultation settings — initialized from context
  const [consultPrice, setConsultPrice] = useState(profile.consultPrice);
  const [consultPriceType, setConsultPriceType] = useState<'fixed' | 'from'>(profile.consultPriceType);
  const [consultDuration, setConsultDuration] = useState(profile.consultDuration);
  const [consultFormats, setConsultFormats] = useState(profile.consultFormats);
  const [consultButtonText, setConsultButtonText] = useState(profile.consultButtonText);
  // Services — initialized from context
  const [services, setServices] = useState(profile.services);
  // Stats block
  const [showStats, setShowStats] = useState(true);
  const statsData = {
    consultations: 1250,
    rating: 94,
    responseTime: 15
  };
  // Reviews settings
  const [reviewsMode, setReviewsMode] = useState<'all' | 'best'>('best');
  const [pinnedReviews] = useState([1, 3]);
  // Products settings
  const [showProducts, setShowProducts] = useState(true);
  const [productsCount, setProductsCount] = useState<3 | 6 | 'all'>(3);
  // Profile blocks (constructor)
  const [blocks, setBlocks] = useState<ProfileBlock[]>([{
    id: 'about',
    label: 'Обо мне',
    enabled: true,
    location: 'Первый текстовый блок под шапкой'
  }, {
    id: 'services',
    label: 'Услуги',
    enabled: true,
    location: 'Блок «Услуги» на странице'
  }, {
    id: 'products',
    label: 'Товары / Курсы',
    enabled: false,
    location: 'Блок «Товары» после услуг'
  }, {
    id: 'reviews',
    label: 'Отзывы',
    enabled: true,
    location: 'Блок «Отзывы клиентов»'
  }, {
    id: 'education',
    label: 'Образование',
    enabled: true,
    location: 'Блок «Образование и сертификаты»'
  }, {
    id: 'faq',
    label: 'FAQ',
    enabled: true,
    location: 'Блок «Частые вопросы» внизу'
  }]);
  // Verification
  const [verificationStatus, setVerificationStatus] = useState<'unverified' | 'pending' | 'verified'>('unverified');
  // Collapsed sections
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({
    contacts: true,
    services: false,
    consultation: true,
    constructor: true,
    stats: true,
    reviews: true,
    products: true,
    verification: true
  });
  const toggle = (s: string) => setCollapsed(p => ({
    ...p,
    [s]: !p[s]
  }));
  // Progress
  const progress = useMemo(() => {
    let score = 0,
      total = 8;
    if (firstName.trim()) score++;
    if (lastName.trim()) score++;
    if (aboutText.trim().length > 50) score++;
    if (specializations.length >= 2) score++;
    if (contacts.some(c => c.enabled && c.value.trim())) score++;
    if (services.length > 0) score++;
    if (experience.trim()) score++;
    if (consultPrice.trim()) score++;
    return Math.round(score / total * 100);
  }, [firstName, lastName, aboutText, specializations, contacts, services, experience, consultPrice]);
  const filteredSpecs = ALL_SPECIALIZATIONS.filter(s => !specializations.includes(s) && s.toLowerCase().includes(specSearch.toLowerCase()));
  const moveBlock = (idx: number, dir: 'up' | 'down') => {
    setBlocks(prev => {
      const next = [...prev];
      const t = dir === 'up' ? idx - 1 : idx + 1;
      if (t < 0 || t >= next.length) return prev;
      [next[idx], next[t]] = [next[t], next[idx]];
      return next;
    });
  };
  const applyRecommendedOrder = () => {
    setBlocks(prev => {
      const sorted = [...prev].sort((a, b) => {
        const ai = RECOMMENDED_ORDER.indexOf(a.id);
        const bi = RECOMMENDED_ORDER.indexOf(b.id);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      });
      return sorted;
    });
    showToast('Рекомендуемый порядок применён');
  };
  const tips = useMemo(() => {
    const t: {
      text: string;
      action: string;
    }[] = [];
    if (aboutText.trim().length < 100) t.push({
      text: 'Добавьте описание (мин. 100 символов)',
      action: 'Написать'
    });
    if (specializations.length < 3) t.push({
      text: 'Добавьте больше специализаций',
      action: 'Добавить'
    });
    if (services.length < 3) t.push({
      text: 'Астрологи с 3+ услугами получают на 30% больше заказов',
      action: 'Добавить'
    });
    if (verificationStatus === 'unverified') t.push({
      text: 'Верификация повышает доверие клиентов',
      action: 'Пройти'
    });
    return t;
  }, [aboutText, specializations, services, verificationStatus]);
  const handleSave = () => {
    const errors: string[] = [];
    if (!firstName.trim() || !lastName.trim()) errors.push('Укажите имя и фамилию');
    if (aboutText.trim().length < 30) errors.push('Описание слишком короткое');
    if (services.length === 0) errors.push('Добавьте хотя бы 1 услугу');
    if (errors.length > 0) {
      showToast(`⚠️ ${errors[0]}`);
      return;
    }
    // Push all changes to shared context (persists to localStorage)
    updateProfile({
      firstName,
      lastName,
      experience,
      languages,
      aboutText,
      specializations,
      avatarUrl,
      services,
      contacts,
      consultPrice,
      consultPriceType,
      consultDuration,
      consultFormats,
      consultButtonText,
      showChatButton,
      showBookButton
    });
    showToast('Изменения сохранены и обновлены в каталоге ✓');
    console.log('[Admin Sync] Profile updated and persisted to context');
  };
  const addCustomSpec = () => {
    const val = customSpecInput.trim();
    if (val && !specializations.includes(val)) {
      setSpecializations(p => [...p, val]);
      setCustomSpecInput('');
      setShowCustomSpecInput(false);
      showToast('Специализация добавлена');
    }
  };
  // Section header helper
  const SectionHeader = ({
    title,
    section,
    badge
  }: {
    title: string;
    section: string;
    badge?: React.ReactNode;
  }) => <div className="flex justify-between items-center cursor-pointer" onClick={() => toggle(section)} data-id="element-14073">
      <h3 className="font-bold text-slate-900 text-sm sm:text-base" data-id="element-14074">{title}</h3>
      <div className="flex items-center gap-2" data-id="element-14075">
        {badge}
        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${!collapsed[section] ? 'rotate-90' : ''}`} data-id="element-14076" />
      </div>
    </div>;
  const SectionBody = ({
    section,
    children
  }: {
    section: string;
    children: React.ReactNode;
  }) => <div className={`overflow-hidden transition-all duration-300 ${!collapsed[section] ? 'max-h-[5000px] mt-5 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-14077">
      {children}
    </div>;
  // Mini preview component
  const MiniPreview = () => <div className={`bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm ${previewDevice === 'mobile' ? 'max-w-[320px] mx-auto' : 'w-full'}`} data-id="element-14078">
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-4 text-white" data-id="element-14079">
        <div className="flex items-center gap-3" data-id="element-14080">
          {avatarUrl ? <img src={avatarUrl} alt="Аватар" className="w-14 h-14 rounded-full object-cover border-2 border-white/30" data-id="element-14081" /> : <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-xl font-bold" data-id="element-14082">
              {firstName.charAt(0)}
              {lastName.charAt(0)}
            </div>}
          <div data-id="element-14083">
            <div className="font-bold text-lg" data-id="element-14084">
              {firstName} {lastName}
            </div>
            <div className="text-indigo-100 text-xs" data-id="element-14085">
              {specializations.slice(0, 2).join(' · ')}
            </div>
            <div className="flex items-center gap-2 mt-1" data-id="element-14086">
              <span className="text-xs" data-id="element-14087">⭐ 4.9</span>
              <span className="text-xs text-indigo-200" data-id="element-14088">
                · {experience} лет опыта
              </span>
            </div>
          </div>
        </div>
      </div>

      {showStats && <div className={`grid grid-cols-3 gap-2 p-3 border-b border-slate-100 ${highlightedBlock === 'stats' ? 'ring-2 ring-indigo-400 bg-indigo-50' : ''}`} data-id="element-14089">
          <div className="text-center" data-id="element-14090">
            <div className="font-bold text-sm text-slate-900" data-id="element-14091">
              {statsData.consultations}+
            </div>
            <div className="text-[10px] text-slate-500" data-id="element-14092">консультаций</div>
          </div>
          <div className="text-center" data-id="element-14093">
            <div className="font-bold text-sm text-slate-900" data-id="element-14094">
              {statsData.rating}%
            </div>
            <div className="text-[10px] text-slate-500" data-id="element-14095">довольны</div>
          </div>
          <div className="text-center" data-id="element-14096">
            <div className="font-bold text-sm text-slate-900" data-id="element-14097">
              {statsData.responseTime} мин
            </div>
            <div className="text-[10px] text-slate-500" data-id="element-14098">ответ</div>
          </div>
        </div>}

      <div className="p-3 space-y-3" data-id="element-14099">
        {blocks.filter(b => b.enabled).map(block => <div key={block.id} className={`p-2 rounded-lg border text-xs transition-all ${highlightedBlock === block.id ? 'border-indigo-400 bg-indigo-50 ring-1 ring-indigo-400' : 'border-slate-100'}`} data-id="element-14100">
              <div className="font-bold text-slate-700 mb-0.5" data-id="element-14101">
                {block.label}
              </div>
              {block.id === 'about' && <div className="text-slate-500 line-clamp-2" data-id="element-14102">
                  {aboutText.slice(0, 80)}...
                </div>}
              {block.id === 'services' && <div className="text-slate-500" data-id="element-14103">
                  {services.length} услуг · от{' '}
                  {consultPriceType === 'from' ? '' : ''}
                  {consultPrice} ₽
                </div>}
              {block.id === 'reviews' && <div className="text-slate-500" data-id="element-14104">⭐ 4.9 · 124 отзыва</div>}
              {block.id === 'products' && <div className="text-slate-500" data-id="element-14105">Курсы и товары</div>}
              {block.id === 'education' && <div className="text-slate-500" data-id="element-14106">Образование и сертификаты</div>}
              {block.id === 'faq' && <div className="text-slate-500" data-id="element-14107">Частые вопросы</div>}
            </div>)}
      </div>

      <div className="p-3 border-t border-slate-100 space-y-2" data-id="element-14108">
        {showBookButton && <div className="w-full py-2 bg-indigo-600 text-white text-center rounded-lg text-xs font-bold" data-id="element-14109">
            {consultButtonText}
          </div>}
        {showChatButton && <div className="w-full py-2 bg-slate-100 text-slate-700 text-center rounded-lg text-xs font-medium" data-id="element-14110">
            Написать в чат
          </div>}
      </div>
    </div>;
  return <div className="space-y-5 pb-20 sm:pb-6" data-id="element-14111">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-14112">
        <div data-id="element-14113">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-14114">
            Редактирование профиля
          </h2>
          <p className="text-sm text-slate-500 mt-0.5" data-id="element-14115">
            Что редактируете → сразу видите на странице клиента
          </p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto" data-id="element-14116">
          <Button variant={showPreview ? 'primary' : 'outline'} size="sm" icon={<Eye className="w-4 h-4" data-id="element-14118" />} onClick={() => setShowPreview(!showPreview)} className="flex-1 sm:flex-none" data-id="element-14117">
            {showPreview ? 'Скрыть превью' : 'Превью'}
          </Button>
          <Button variant="outline" size="sm" icon={<ExternalLink className="w-4 h-4" data-id="element-14120" />} onClick={() => onNavigate('astrologer-profile')} className="flex-1 sm:flex-none" data-id="element-14119">
            Открыть страницу
          </Button>
          <Button variant="primary" size="sm" icon={<Save className="w-4 h-4" data-id="element-14122" />} onClick={handleSave} className="flex-1 sm:flex-none" data-id="element-14121">
            Сохранить
          </Button>
        </div>
      </div>

      {/* Progress */}
      <Card padding="sm" className={progress === 100 ? 'border-emerald-200 bg-emerald-50/30' : 'border-amber-200 bg-amber-50/30'} data-id="element-14123">
        <div className="flex items-center justify-between mb-1.5" data-id="element-14124">
          <span className="text-sm font-bold text-slate-900" data-id="element-14125">
            Заполнение профиля
          </span>
          <span className={`text-sm font-bold ${progress === 100 ? 'text-emerald-600' : 'text-amber-600'}`} data-id="element-14126">
            {progress}%
          </span>
        </div>
        <div className="w-full bg-slate-200 rounded-full h-2" data-id="element-14127">
          <div className={`h-2 rounded-full transition-all duration-500 ${progress === 100 ? 'bg-emerald-500' : progress >= 70 ? 'bg-amber-400' : 'bg-rose-400'}`} style={{
          width: `${progress}%`
        }} data-id="element-14128" />
        </div>
        {progress < 100 && <p className="text-xs text-amber-700 mt-1.5" data-id="element-14129">
            Заполните полностью → до +40% заказов
          </p>}
      </Card>

      {/* Tips */}
      {tips.length > 0 && <Card padding="sm" data-id="element-14130">
          <div className="flex items-center gap-2 mb-2" data-id="element-14131">
            <Lightbulb className="w-4 h-4 text-amber-500" data-id="element-14132" />
            <span className="text-sm font-bold text-slate-900" data-id="element-14133">
              Как улучшить профиль
            </span>
          </div>
          <div className="space-y-1.5" data-id="element-14134">
            {tips.slice(0, 3).map((tip, i) => <div key={i} className="flex items-center justify-between gap-3 text-sm" data-id="element-14135">
                <span className="text-slate-600 flex-1" data-id="element-14136">{tip.text}</span>
                <button className="text-indigo-600 font-medium text-xs hover:text-indigo-700 shrink-0" data-id="element-14137">
                  {tip.action} →
                </button>
              </div>)}
          </div>
        </Card>}

      {/* Main layout: Editor + Preview */}
      <div className={`flex gap-6 ${showPreview ? 'flex-col lg:flex-row' : ''}`} data-id="element-14138">
        {/* Editor Column */}
        <div className={`space-y-5 ${showPreview ? 'flex-1 min-w-0' : 'w-full'}`} data-id="element-14139">
          {/* Main Info */}
          <Card padding="md" data-id="element-14140">
            <div className="flex justify-between items-center mb-4" data-id="element-14141">
              <h3 className="font-bold text-slate-900" data-id="element-14142">Основная информация</h3>
              <Badge variant="neutral" className="bg-slate-100 text-[10px]" data-id="element-14143">
                <Lock className="w-3 h-3 mr-1" data-id="element-14144" /> Обязательно
              </Badge>
            </div>
            <p className="text-[11px] text-indigo-600 mb-4 flex items-center gap-1" data-id="element-14145">
              <MapPin className="w-3 h-3" data-id="element-14146" /> Отображается: верх профиля (имя,
              рейтинг, опыт)
            </p>

            <div className="flex flex-col md:flex-row gap-5 mb-5" data-id="element-14147">
              <div className="flex flex-col items-center gap-2 w-28 shrink-0" data-id="element-14148">
                <div className="relative w-28 h-28 rounded-xl bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden group" data-id="element-14149">
                  {avatarUrl ? <img src={avatarUrl} alt="Аватар" className="w-full h-full object-cover" data-id="element-14150" /> : <div className="w-full h-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center" data-id="element-14151">
                      <span className="text-3xl font-bold text-indigo-300" data-id="element-14152">
                        {firstName.charAt(0)}
                        {lastName.charAt(0)}
                      </span>
                    </div>}
                  <button onClick={handleAvatarUpload} className="absolute inset-0 bg-slate-900/50 flex items-center justify-center opacity-0 group-hover:opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity" data-id="element-14153">
                    <Camera className="w-5 h-5 text-white" data-id="element-14154" />
                  </button>
                </div>
                <button onClick={handleAvatarUpload} className="text-xs text-indigo-600 font-medium hover:underline" data-id="element-14155">
                  {avatarUrl ? 'Заменить' : 'Загрузить фото'}
                </button>
                {avatarUrl && <button onClick={() => {
                setAvatarUrl(null);
                showToast('Фото удалено');
              }} className="text-xs text-rose-500 font-medium hover:underline" data-id="element-14156">
                    Удалить
                  </button>}
              </div>
              <div className="flex-1 space-y-3" data-id="element-14157">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-14158">
                  <div data-id="element-14159">
                    <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-14160">
                      Имя
                    </label>
                    <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full rounded-lg border-slate-200 px-3 py-2 border focus:ring-2 focus:ring-indigo-500 outline-none text-sm" data-id="element-14161" />
                  </div>
                  <div data-id="element-14162">
                    <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-14163">
                      Фамилия
                    </label>
                    <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className="w-full rounded-lg border-slate-200 px-3 py-2 border focus:ring-2 focus:ring-indigo-500 outline-none text-sm" data-id="element-14164" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-14165">
                  <div data-id="element-14166">
                    <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-14167">
                      Опыт (лет)
                    </label>
                    <input type="number" value={experience} onChange={e => setExperience(e.target.value)} className="w-full rounded-lg border-slate-200 px-3 py-2 border focus:ring-2 focus:ring-indigo-500 outline-none text-sm" data-id="element-14168" />
                  </div>
                  <div data-id="element-14169">
                    <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-14170">
                      Языки
                    </label>
                    <input type="text" value={languages} onChange={e => setLanguages(e.target.value)} className="w-full rounded-lg border-slate-200 px-3 py-2 border focus:ring-2 focus:ring-indigo-500 outline-none text-sm" data-id="element-14171" />
                  </div>
                </div>
              </div>
            </div>

            {/* Specializations */}
            <div className="mb-5" data-id="element-14172">
              <label className="block text-xs font-medium text-slate-600 mb-2" data-id="element-14173">
                Специализации
              </label>
              <div className="flex flex-wrap gap-1.5 mb-2" data-id="element-14174">
                {specializations.map(tag => <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-medium border border-indigo-100" data-id="element-14175">
                    {tag}{' '}
                    <X className="w-3 h-3 cursor-pointer hover:text-rose-500" onClick={() => setSpecializations(p => p.filter(s => s !== tag))} data-id="element-14176" />
                  </span>)}
              </div>
              <div className="flex flex-col sm:flex-row gap-2" data-id="element-14177">
                <div className="relative flex-1 min-w-0" data-id="element-14178">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-14179" />
                  <input type="text" value={specSearch} onChange={e => {
                  setSpecSearch(e.target.value);
                  setShowSpecDropdown(true);
                }} onFocus={() => setShowSpecDropdown(true)} placeholder="Поиск..." className="w-full pl-8 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-14180" />
                  {showSpecDropdown && filteredSpecs.length > 0 && <div className="absolute z-10 top-full mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-36 overflow-y-auto" data-id="element-14181">
                      {filteredSpecs.map(s => <button key={s} onClick={() => {
                    setSpecializations(p => [...p, s]);
                    setSpecSearch('');
                    setShowSpecDropdown(false);
                  }} className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 hover:text-indigo-700" data-id="element-14182">
                          {s}
                        </button>)}
                    </div>}
                </div>
                {showCustomSpecInput ? <div className="flex items-center gap-1" data-id="element-14183">
                    <input type="text" value={customSpecInput} onChange={e => setCustomSpecInput(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter') addCustomSpec();
                  if (e.key === 'Escape') setShowCustomSpecInput(false);
                }} placeholder="Своя..." className="w-32 rounded-lg border-slate-200 px-3 py-2 border text-sm focus:ring-2 focus:ring-indigo-500 outline-none" autoFocus data-id="element-14184" />
                    <button onClick={addCustomSpec} className="p-1.5 text-emerald-500 hover:text-emerald-700" data-id="element-14185">
                      <Check className="w-4 h-4" data-id="element-14186" />
                    </button>
                    <button onClick={() => {
                  setShowCustomSpecInput(false);
                  setCustomSpecInput('');
                }} className="p-1.5 text-slate-400 hover:text-slate-600" data-id="element-14187">
                      <X className="w-4 h-4" data-id="element-14188" />
                    </button>
                  </div> : <Button variant="outline" size="sm" onClick={() => setShowCustomSpecInput(true)} icon={<Plus className="w-3.5 h-3.5" data-id="element-14190" />} data-id="element-14189">
                    Своя
                  </Button>}
              </div>
            </div>

            {/* About */}
            <div data-id="element-14191">
              <div className="flex justify-between items-center mb-1" data-id="element-14192">
                <label className="text-xs font-medium text-slate-600" data-id="element-14193">
                  О себе
                </label>
                <button onClick={() => setShowAboutHint(!showAboutHint)} className="text-[11px] text-indigo-600 hover:text-indigo-700 font-medium" data-id="element-14194">
                  {showAboutHint ? 'Скрыть' : 'Что писать?'}
                </button>
              </div>
              <p className="text-[11px] text-indigo-600 mb-2 flex items-center gap-1" data-id="element-14195">
                <MapPin className="w-3 h-3" data-id="element-14196" /> Отображается: первый текстовый
                блок под шапкой
              </p>
              {showAboutHint && <div className="mb-2 p-2.5 bg-indigo-50 border border-indigo-100 rounded-lg text-xs text-indigo-700 space-y-1" data-id="element-14197">
                  <p data-id="element-14198">
                    Расскажите о себе: опыт, подход, чем отличаетесь. Клиенты
                    выбирают тех, кому доверяют.
                  </p>
                  <p className="text-indigo-500 italic" data-id="element-14199">
                    Пример: «Привет! Я Елена, астролог с 8-летним стажем. Мой
                    подход — перевести язык звёзд на понятный человеческий.»
                  </p>
                </div>}
              <textarea className="w-full rounded-lg border-slate-200 px-3 py-2.5 border focus:ring-2 focus:ring-indigo-500 outline-none text-sm resize-none" rows={3} value={aboutText} onChange={e => setAboutText(e.target.value.slice(0, MAX_ABOUT_LENGTH))} onFocus={() => setHighlightedBlock('about')} onBlur={() => setHighlightedBlock(null)} data-id="element-14200" />
              <div className="flex justify-end mt-0.5" data-id="element-14201">
                <span className={`text-[11px] ${aboutText.length > 900 ? 'text-rose-500' : 'text-slate-400'}`} data-id="element-14202">
                  {aboutText.length}/{MAX_ABOUT_LENGTH}
                </span>
              </div>
            </div>
          </Card>

          {/* Consultation Settings */}
          <Card padding="md" data-id="element-14203">
            <SectionHeader title="Запись на консультацию" section="consultation" badge={<Badge variant="success" size="sm" data-id="element-14205">
                  Ключевой блок
                </Badge>} data-id="element-14204" />
            <SectionBody section="consultation" data-id="element-14206">
              <p className="text-[11px] text-indigo-600 mb-3 flex items-center gap-1" data-id="element-14207">
                <MapPin className="w-3 h-3" data-id="element-14208" /> Отображается: боковая панель
                справа (кнопка записи)
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3" data-id="element-14209">
                <div data-id="element-14210">
                  <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-14211">
                    Цена
                  </label>
                  <div className="flex gap-2" data-id="element-14212">
                    <select value={consultPriceType} onChange={e => setConsultPriceType(e.target.value as 'fixed' | 'from')} className="rounded-lg border-slate-200 px-2 py-2 border text-sm focus:ring-2 focus:ring-indigo-500 outline-none" data-id="element-14213">
                      <option value="from" data-id="element-14214">от</option>
                      <option value="fixed" data-id="element-14215">фикс</option>
                    </select>
                    <input type="number" value={consultPrice} onChange={e => setConsultPrice(e.target.value)} className="flex-1 rounded-lg border-slate-200 px-3 py-2 border text-sm focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="5000" data-id="element-14216" />
                    <span className="flex items-center text-sm text-slate-500" data-id="element-14217">
                      ₽
                    </span>
                  </div>
                </div>
                <div data-id="element-14218">
                  <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-14219">
                    Длительность (мин)
                  </label>
                  <input type="number" value={consultDuration} onChange={e => setConsultDuration(e.target.value)} className="w-full rounded-lg border-slate-200 px-3 py-2 border text-sm focus:ring-2 focus:ring-indigo-500 outline-none" data-id="element-14220" />
                </div>
              </div>
              <div className="mb-3" data-id="element-14221">
                <label className="block text-xs font-medium text-slate-600 mb-2" data-id="element-14222">
                  Формат
                </label>
                <div className="flex gap-2" data-id="element-14223">
                  {[{
                  key: 'video' as const,
                  icon: Video,
                  label: 'Видео'
                }, {
                  key: 'chat' as const,
                  icon: MessageSquare,
                  label: 'Чат'
                }, {
                  key: 'audio' as const,
                  icon: Mic,
                  label: 'Аудио'
                }].map(f => <button key={f.key} onClick={() => setConsultFormats(p => ({
                  ...p,
                  [f.key]: !p[f.key]
                }))} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${consultFormats[f.key] ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`} data-id="element-14224">
                      <f.icon className="w-4 h-4" data-id="element-14225" /> {f.label}
                    </button>)}
                </div>
              </div>
              <div data-id="element-14226">
                <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-14227">
                  Текст кнопки
                </label>
                <input type="text" value={consultButtonText} onChange={e => setConsultButtonText(e.target.value)} className="w-full rounded-lg border-slate-200 px-3 py-2 border text-sm focus:ring-2 focus:ring-indigo-500 outline-none" data-id="element-14228" />
              </div>
            </SectionBody>
          </Card>

          {/* Contacts */}
          <Card padding="md" data-id="element-14229">
            <SectionHeader title="Контакты и кнопки связи" section="contacts" data-id="element-14230" />
            <SectionBody section="contacts" data-id="element-14231">
              <p className="text-[11px] text-indigo-600 mb-3 flex items-center gap-1" data-id="element-14232">
                <MapPin className="w-3 h-3" data-id="element-14233" /> Отображается: блок «Связаться» на
                странице
              </p>
              <div className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg mb-3" data-id="element-14234">
                <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" data-id="element-14235" />
                <p className="text-xs text-amber-700" data-id="element-14236">
                  Не уводите клиентов с платформы. Заказы через AstroGuide —
                  безопасность для вас и клиентов.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 sm:gap-3 mb-4" data-id="element-14237">
                <button onClick={() => setShowChatButton(!showChatButton)} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs sm:text-sm font-medium whitespace-nowrap ${showChatButton ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-500'}`} data-id="element-14238">
                  {showChatButton ? <ToggleRight className="w-5 h-5 text-emerald-500 flex-shrink-0" data-id="element-14239" /> : <ToggleLeft className="w-5 h-5 text-slate-300 flex-shrink-0" data-id="element-14240" />}{' '}
                  Кнопка «Написать»
                </button>
                <button onClick={() => setShowBookButton(!showBookButton)} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs sm:text-sm font-medium whitespace-nowrap ${showBookButton ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-500'}`} data-id="element-14241">
                  {showBookButton ? <ToggleRight className="w-5 h-5 text-emerald-500 flex-shrink-0" data-id="element-14242" /> : <ToggleLeft className="w-5 h-5 text-slate-300 flex-shrink-0" data-id="element-14243" />}{' '}
                  Кнопка «Записаться»
                </button>
              </div>
              <div className="space-y-2" data-id="element-14244">
                {contacts.map((c, idx) => <div key={c.id} className={`flex items-center gap-2.5 p-2.5 rounded-lg border transition-colors ${c.enabled ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-50'}`} data-id="element-14245">
                    <button onClick={() => setContacts(prev => prev.map((ct, i) => i === idx ? {
                  ...ct,
                  enabled: !ct.enabled
                } : ct))} className="shrink-0" data-id="element-14246">
                      {c.enabled ? <ToggleRight className="w-5 h-5 text-emerald-500" data-id="element-14247" /> : <ToggleLeft className="w-5 h-5 text-slate-300" data-id="element-14248" />}
                    </button>
                    <span className={`w-6 h-6 rounded flex items-center justify-center text-[9px] font-bold shrink-0 ${c.color}`} data-id="element-14249">
                      {c.code}
                    </span>
                    <input type="text" value={c.value} onChange={e => setContacts(prev => prev.map((ct, i) => i === idx ? {
                  ...ct,
                  value: e.target.value
                } : ct))} placeholder={c.label} disabled={!c.enabled} className="flex-1 rounded-lg border-slate-200 px-2.5 py-1.5 border text-sm focus:ring-2 focus:ring-indigo-500 outline-none disabled:bg-slate-50 disabled:text-slate-400 min-w-0" data-id="element-14250" />
                  </div>)}
              </div>
            </SectionBody>
          </Card>

          {/* Stats Block */}
          <Card padding="md" data-id="element-14251">
            <SectionHeader title="Блок статистики" section="stats" data-id="element-14252" />
            <SectionBody section="stats" data-id="element-14253">
              <p className="text-[11px] text-indigo-600 mb-3 flex items-center gap-1" data-id="element-14254">
                <MapPin className="w-3 h-3" data-id="element-14255" /> Отображается: под шапкой (1250+,
                94%, 15 мин)
              </p>
              <div className="flex items-center gap-3 mb-3" data-id="element-14256">
                <button onClick={() => setShowStats(!showStats)} className="shrink-0" data-id="element-14257">
                  {showStats ? <ToggleRight className="w-6 h-6 text-emerald-500" data-id="element-14258" /> : <ToggleLeft className="w-6 h-6 text-slate-300" data-id="element-14259" />}
                </button>
                <span className="text-sm font-medium text-slate-700" data-id="element-14260">
                  {showStats ? 'Блок включён' : 'Блок выключен'}
                </span>
              </div>
              {showStats && <div className="grid grid-cols-3 gap-3" data-id="element-14261">
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 text-center" data-id="element-14262">
                    <div className="text-lg font-bold text-slate-900" data-id="element-14263">
                      {statsData.consultations}+
                    </div>
                    <div className="text-[10px] text-slate-500" data-id="element-14264">
                      Консультаций (авто)
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 text-center" data-id="element-14265">
                    <div className="text-lg font-bold text-slate-900" data-id="element-14266">
                      {statsData.rating}%
                    </div>
                    <div className="text-[10px] text-slate-500" data-id="element-14267">
                      Довольны (авто)
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 text-center" data-id="element-14268">
                    <div className="text-lg font-bold text-slate-900" data-id="element-14269">
                      {statsData.responseTime} мин
                    </div>
                    <div className="text-[10px] text-slate-500" data-id="element-14270">
                      Время ответа (авто)
                    </div>
                  </div>
                </div>}
            </SectionBody>
          </Card>

          {/* Services */}
          <Card padding="md" data-id="element-14271">
            <SectionHeader title="Услуги" section="services" data-id="element-14272" />
            <SectionBody section="services" data-id="element-14273">
              <p className="text-[11px] text-indigo-600 mb-3 flex items-center gap-1" data-id="element-14274">
                <MapPin className="w-3 h-3" data-id="element-14275" /> Отображается: блок «Услуги» на
                странице
              </p>
              <div className="space-y-2 mb-3" data-id="element-14276">
                {services.map((srv, idx) => <div key={srv.id} className={`flex items-center gap-2.5 p-3 rounded-lg border ${srv.isTop ? 'border-amber-300 bg-amber-50/30' : 'border-slate-100 bg-slate-50/50'}`} onMouseEnter={() => setHighlightedBlock('services')} onMouseLeave={() => setHighlightedBlock(null)} data-id="element-14277">
                    <div className="flex flex-col gap-0.5 shrink-0" data-id="element-14278">
                      <button onClick={() => {
                    const n = [...services];
                    if (idx > 0) {
                      ;
                      [n[idx], n[idx - 1]] = [n[idx - 1], n[idx]];
                      setServices(n);
                    }
                  }} disabled={idx === 0} className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30" data-id="element-14279">
                        <ChevronUp className="w-3.5 h-3.5" data-id="element-14280" />
                      </button>
                      <button onClick={() => {
                    const n = [...services];
                    if (idx < n.length - 1) {
                      ;
                      [n[idx], n[idx + 1]] = [n[idx + 1], n[idx]];
                      setServices(n);
                    }
                  }} disabled={idx === services.length - 1} className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30" data-id="element-14281">
                        <ChevronDown className="w-3.5 h-3.5" data-id="element-14282" />
                      </button>
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-14283">
                      <div className="font-medium text-slate-900 text-sm flex items-center gap-1.5" data-id="element-14284">
                        {srv.isTop && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" data-id="element-14285" />}
                        {srv.name}
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-14286">
                        {srv.price} · {srv.duration}
                      </div>
                    </div>
                    <button onClick={() => setServices(p => p.map(s => s.id === srv.id ? {
                  ...s,
                  isTop: !s.isTop
                } : s))} className={`p-1.5 rounded-lg transition-colors ${srv.isTop ? 'text-amber-500 bg-amber-100' : 'text-slate-400 hover:text-amber-500 hover:bg-amber-50'}`} title="Выделить как топ" data-id="element-14287">
                      <Star className="w-4 h-4" data-id="element-14288" />
                    </button>
                    <Button variant="ghost" size="sm" className="text-indigo-600 shrink-0" onClick={() => {
                  if (setActiveTab) setActiveTab('services');else showToast(`Редактирование: ${srv.name}`);
                }} data-id="element-14289">
                      Изменить
                    </Button>
                  </div>)}
              </div>
              <Button variant="primary" size="sm" icon={<Plus className="w-3.5 h-3.5" data-id="element-14291" />} onClick={() => {
              if (setActiveTab) setActiveTab('services');else showToast('Создание услуги');
            }} data-id="element-14290">
                Добавить услугу
              </Button>
            </SectionBody>
          </Card>

          {/* Reviews Settings */}
          <Card padding="md" data-id="element-14292">
            <SectionHeader title="Отзывы" section="reviews" data-id="element-14293" />
            <SectionBody section="reviews" data-id="element-14294">
              <p className="text-[11px] text-indigo-600 mb-3 flex items-center gap-1" data-id="element-14295">
                <MapPin className="w-3 h-3" data-id="element-14296" /> Отображается: блок «Отзывы
                клиентов»
              </p>
              <div className="flex gap-2 mb-3" data-id="element-14297">
                {(['all', 'best'] as const).map(m => <button key={m} onClick={() => setReviewsMode(m)} className={`px-3 py-2 rounded-lg border text-sm font-medium ${reviewsMode === m ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-500'}`} data-id="element-14298">
                    {m === 'all' ? 'Все отзывы' : 'Лучшие (4-5 ⭐)'}
                  </button>)}
              </div>
              <p className="text-xs text-slate-500" data-id="element-14299">
                Закреплённых отзывов: {pinnedReviews.length}. Управление
                отзывами → вкладка «Отзывы».
              </p>
            </SectionBody>
          </Card>

          {/* Products Settings */}
          <Card padding="md" data-id="element-14300">
            <SectionHeader title="Товары / Курсы" section="products" data-id="element-14301" />
            <SectionBody section="products" data-id="element-14302">
              <p className="text-[11px] text-indigo-600 mb-3 flex items-center gap-1" data-id="element-14303">
                <MapPin className="w-3 h-3" data-id="element-14304" /> Отображается: блок «Товары» после
                услуг
              </p>
              <div className="flex items-center gap-3 mb-3" data-id="element-14305">
                <button onClick={() => setShowProducts(!showProducts)} data-id="element-14306">
                  {showProducts ? <ToggleRight className="w-6 h-6 text-emerald-500" data-id="element-14307" /> : <ToggleLeft className="w-6 h-6 text-slate-300" data-id="element-14308" />}
                </button>
                <span className="text-sm font-medium text-slate-700" data-id="element-14309">
                  {showProducts ? 'Блок включён' : 'Блок выключен'}
                </span>
              </div>
              {showProducts && <div data-id="element-14310">
                  <label className="block text-xs font-medium text-slate-600 mb-2" data-id="element-14311">
                    Показывать
                  </label>
                  <div className="flex gap-2" data-id="element-14312">
                    {([3, 6, 'all'] as const).map(n => <button key={String(n)} onClick={() => setProductsCount(n)} className={`px-3 py-1.5 rounded-lg border text-sm font-medium ${productsCount === n ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-500'}`} data-id="element-14313">
                        {n === 'all' ? 'Все' : `${n} шт`}
                      </button>)}
                  </div>
                </div>}
            </SectionBody>
          </Card>

          {/* Profile Constructor */}
          <Card padding="md" data-id="element-14314">
            <SectionHeader title="Конструктор профиля" section="constructor" data-id="element-14315" />
            <SectionBody section="constructor" data-id="element-14316">
              <div className="flex justify-between items-center mb-3" data-id="element-14317">
                <p className="text-xs text-slate-500" data-id="element-14318">
                  Порядок и видимость блоков на публичной странице
                </p>
                <Button variant="ghost" size="sm" className="text-indigo-600" icon={<Sparkles className="w-3.5 h-3.5" data-id="element-14320" />} onClick={applyRecommendedOrder} data-id="element-14319">
                  Рекомендуемый
                </Button>
              </div>
              <div className="space-y-1.5" data-id="element-14321">
                {blocks.map((block, idx) => <div key={block.id} className={`flex items-center gap-2.5 p-2.5 rounded-lg border transition-colors ${block.enabled ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-50'}`} onMouseEnter={() => setHighlightedBlock(block.id)} onMouseLeave={() => setHighlightedBlock(null)} data-id="element-14322">
                    <div className="flex flex-col gap-0.5 shrink-0" data-id="element-14323">
                      <button onClick={() => moveBlock(idx, 'up')} disabled={idx === 0} className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30" data-id="element-14324">
                        <ChevronUp className="w-3.5 h-3.5" data-id="element-14325" />
                      </button>
                      <button onClick={() => moveBlock(idx, 'down')} disabled={idx === blocks.length - 1} className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30" data-id="element-14326">
                        <ChevronDown className="w-3.5 h-3.5" data-id="element-14327" />
                      </button>
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-14328">
                      <span className="text-sm font-medium text-slate-900" data-id="element-14329">
                        {block.label}
                      </span>
                      <div className="text-[10px] text-slate-400" data-id="element-14330">
                        {block.location}
                      </div>
                    </div>
                    <button onClick={() => {
                  setHighlightedBlock(block.id);
                  setTimeout(() => setHighlightedBlock(null), 2000);
                }} className="p-1 text-slate-400 hover:text-indigo-600" title="Показать на превью" data-id="element-14331">
                      <Eye className="w-3.5 h-3.5" data-id="element-14332" />
                    </button>
                    <button onClick={() => setBlocks(prev => prev.map(b => b.id === block.id ? {
                  ...b,
                  enabled: !b.enabled
                } : b))} data-id="element-14333">
                      {block.enabled ? <ToggleRight className="w-5 h-5 text-emerald-500" data-id="element-14334" /> : <ToggleLeft className="w-5 h-5 text-slate-300" data-id="element-14335" />}
                    </button>
                  </div>)}
              </div>
            </SectionBody>
          </Card>

          {/* Verification */}
          <Card padding="md" className="border-indigo-100 bg-indigo-50/30" data-id="element-14336">
            <SectionHeader title="Верификация" section="verification" badge={verificationStatus === 'unverified' ? <Badge variant="warning" size="sm" data-id="element-14338">
                    Не пройдена
                  </Badge> : verificationStatus === 'pending' ? <Badge variant="pro" size="sm" data-id="element-14339">
                    На проверке
                  </Badge> : <Badge variant="success" size="sm" data-id="element-14340">
                    Пройдена
                  </Badge>} data-id="element-14337" />
            <SectionBody section="verification" data-id="element-14341">
              <p className="text-xs text-slate-600 mb-3" data-id="element-14342">
                Верифицированные профили получают значок PRO и больше доверия.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3" data-id="element-14343">
                {[{
                icon: FileCheck,
                title: 'Паспорт / ID'
              }, {
                icon: GraduationCap,
                title: 'Диплом'
              }, {
                icon: FileText,
                title: 'Доп. документы'
              }].map(doc => <div key={doc.title} className="border-2 border-dashed border-slate-300 rounded-lg p-3 text-center hover:bg-white hover:border-indigo-300 cursor-pointer transition-colors" onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/*,.pdf';
                input.onchange = e => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (file) {
                    showToast(`${doc.title}: "${file.name}" загружен ✓`);
                  }
                };
                input.click();
              }} data-id="element-14344">
                    <doc.icon className="w-5 h-5 text-slate-400 mx-auto mb-1" data-id="element-14345" />
                    <div className="text-xs font-bold text-slate-900" data-id="element-14346">
                      {doc.title}
                    </div>
                    <span className="text-[10px] text-indigo-600 font-medium" data-id="element-14347">
                      Загрузить
                    </span>
                  </div>)}
              </div>
              <div className="flex justify-end" data-id="element-14348">
                <Button variant="primary" size="sm" disabled={verificationStatus !== 'unverified'} onClick={() => {
                setVerificationStatus('pending');
                showToast('Документы отправлены ✓');
              }} data-id="element-14349">
                  {verificationStatus === 'unverified' ? 'Отправить на проверку' : verificationStatus === 'pending' ? 'На проверке...' : 'Верифицирован ✓'}
                </Button>
              </div>
            </SectionBody>
          </Card>
        </div>

        {/* Preview Column */}
        {showPreview && <div className="lg:w-[360px] shrink-0 lg:sticky lg:top-4 lg:self-start" data-id="element-14350">
            <div className="flex items-center justify-between mb-3" data-id="element-14351">
              <span className="text-sm font-bold text-slate-900" data-id="element-14352">
                Предпросмотр
              </span>
              <div className="flex bg-slate-100 p-0.5 rounded-lg" data-id="element-14353">
                <button onClick={() => setPreviewDevice('desktop')} className={`p-1.5 rounded-md ${previewDevice === 'desktop' ? 'bg-white shadow-sm' : ''}`} data-id="element-14354">
                  <Monitor className="w-4 h-4 text-slate-600" data-id="element-14355" />
                </button>
                <button onClick={() => setPreviewDevice('mobile')} className={`p-1.5 rounded-md ${previewDevice === 'mobile' ? 'bg-white shadow-sm' : ''}`} data-id="element-14356">
                  <Smartphone className="w-4 h-4 text-slate-600" data-id="element-14357" />
                </button>
              </div>
            </div>
            <MiniPreview data-id="element-14358" />
          </div>}
      </div>

      {/* Mobile Sticky Save */}
      <div className="fixed bottom-0 left-0 right-0 p-3 bg-white border-t border-slate-200 sm:hidden z-40 shadow-lg" data-id="element-14359">
        <Button variant="primary" fullWidth icon={<Save className="w-4 h-4" data-id="element-14361" />} onClick={handleSave} data-id="element-14360">
          Сохранить
        </Button>
      </div>
    </div>;
}